// PWA Registration using vite-plugin-pwa
// This file handles Service Worker registration and auto-update functionality

import { registerSW } from 'virtual:pwa-register';
import { logError } from './logger';

// Types for update detection
interface PWARegistrationOptions {
  onRegistered?: (registration?: ServiceWorkerRegistration) => void;
  onRegisterError?: (error: unknown) => void;
  onOfflineReady?: () => void;
  onNeedRefresh?: () => void;
  onUpdateFound?: (registration?: ServiceWorkerRegistration) => void;
}

// Initialize PWA registration with enhanced auto-update (legacy support)
export const initializePWA = (options: PWARegistrationOptions = {}) => {
  if (typeof window === 'undefined') {
    console.warn('🔧 PWA: Skipping initialization - not in browser environment');
    return;
  }

  console.warn('🚀 PWA: Starting legacy initialization...');

  const {
    onRegistered,
    onRegisterError,
    onOfflineReady,
    onNeedRefresh,
    onUpdateFound
  } = options;

  // Check if online before attempting registration
  const isOnline = navigator.onLine;
  console.warn(`🌐 PWA: Network status - ${isOnline ? 'Online' : 'Offline'}`);

  // Register Service Worker with auto-update
  const updateSW = registerSW({
    immediate: true,
    onRegistered: (registration: ServiceWorkerRegistration | undefined) => {
      console.warn('✅ PWA: Service Worker registered successfully (legacy)', registration);
      onRegistered?.(registration);

      // Set up periodic update checks (every 60 seconds) only when online
      if (registration) {
        const checkForUpdates = () => {
          if (navigator.onLine) {
            console.warn('🔄 PWA: Checking for updates...');
            registration.update().catch((error) => {
              console.warn('⚠️ PWA: Update check failed', error);
            });
          } else {
            console.warn('📴 PWA: Skipping update check - offline');
          }
        };

        setInterval(checkForUpdates, 60000);

        // Initial update check after 5 seconds
        setTimeout(checkForUpdates, 5000);
      }
    },
    onRegisterError: (error: unknown) => {
      console.error('❌ PWA: Service Worker registration failed (legacy)', error);
      logError('Service Worker registration failed', 'PWA', error);
      onRegisterError?.(error);
    },
    onOfflineReady: () => {
      console.warn('📱 PWA: App ready to work offline (legacy)');
      onOfflineReady?.();
    },
    onNeedRefresh: () => {
      console.warn('🔄 PWA: New content available (legacy)');
      onNeedRefresh?.();
    },
  });

  // Additional update detection for better UX
  if ('serviceWorker' in navigator) {
    // Listen for controller changes
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.warn('🔄 PWA: Service Worker controller changed - reloading page');
      window.location.reload();
    });

    // Listen for network status changes
    window.addEventListener('online', () => {
      console.warn('🌐 PWA: Back online - checking for updates');
      navigator.serviceWorker.ready.then((registration) => {
        registration.update().catch(() => {
          console.warn('⚠️ PWA: Update check failed after coming online');
        });
      });
    });

    window.addEventListener('offline', () => {
      console.warn('📴 PWA: Gone offline');
    });

    // Listen for update found events
    navigator.serviceWorker.ready.then((registration) => {
      const waiting = registration.waiting;
      if (waiting) {
        console.warn('🔄 PWA: Service Worker waiting - update available');
        onUpdateFound?.(registration);
      }

      registration.addEventListener('updatefound', () => {
        console.warn('🔍 PWA: Update found - new Service Worker installing');
        const newWorker = registration.installing;
        if (newWorker) {
          onUpdateFound?.(registration);

          newWorker.addEventListener('statechange', () => {
            console.warn(`🔄 PWA: Service Worker state changed to: ${newWorker.state}`);
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.warn('🎉 PWA: New Service Worker installed - refresh needed');
              onNeedRefresh?.();
            }
          });
        }
      });
    });
  }

  return updateSW;
};

// Force update when user accepts
export const updateApp = (updateSW?: () => Promise<void>) => {
  if (updateSW) {
    updateSW().catch((error) => {
      logError('PWA update failed', 'PWA', error);
    });
  }
};

// Check if app is running as PWA
export const isPWA = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
         document.referrer.includes('android-app://');
};

// Check if service worker is supported
export const isPWASupported = (): boolean => {
  return 'serviceWorker' in navigator &&
         'PushManager' in window &&
         'Notification' in window;
};

// Interface for beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Get install prompt event for PWA installation
let deferredPrompt: BeforeInstallPromptEvent | null = null;

// Listen for install prompt
window.addEventListener('beforeinstallprompt', (e: Event) => {
  e.preventDefault();
  deferredPrompt = e as BeforeInstallPromptEvent;
});

// Show install prompt
export const showInstallPrompt = async (): Promise<boolean> => {
  if (!deferredPrompt) {
    return false;
  }

  try {
    await deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;
    deferredPrompt = null;
    return choiceResult.outcome === 'accepted';
  } catch (error) {
    logError('PWA install prompt failed', 'PWA', error);
    return false;
  }
};

// Check if install prompt is available
export const canInstallPWA = (): boolean => {
  return deferredPrompt !== null;
};